import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthContext, User } from './AuthContext';
import * as authService from './authService';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getAccessToken();
      if (token && !authService.isTokenExpired(token)) {
        // Token exists and is valid - get user info from token
        const tokenUser = authService.getUserFromToken(token);
        if (tokenUser) {
          // Fetch full user data
          try {
            const fullUser = await authService.getCurrentUser();
            if (fullUser) {
              setUser(fullUser);
            } else {
              setUser({
                id: tokenUser.id,
                email: tokenUser.email,
                username: tokenUser.username,
                created_at: new Date().toISOString(),
              });
            }
          } catch {
            // Use token data as fallback
            setUser({
              id: tokenUser.id,
              email: tokenUser.email,
              username: tokenUser.username,
              created_at: new Date().toISOString(),
            });
          }
        }
      } else if (token) {
        // Token expired - try to refresh
        try {
          const response = await authService.refreshTokens();
          if (response) {
            setUser(response.user);
          }
        } catch {
          authService.clearTokens();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authService.login(email, password);
    setUser(response.user);
  }, []);

  const signup = useCallback(async (email: string, username: string, password: string) => {
    const response = await authService.signup(email, username, password);
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    window.location.href = authService.getGoogleAuthUrl();
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    loginWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
